import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { request } from '../../api/client.js';
import { useAuthStore, useCanConfigure } from '../../stores/auth.store.js';
import { toolPreparationPath } from './ToolLink';
import { canonicalNavigationPath } from '../../lib/navigation-preferences.js';
export type NativeId = 'crm' | 'atendimento' | 'agenda' | 'conhecimento' | 'automacoes';
const paths: Record<string, NativeId> = {
  '/contatos': 'crm',
  '/oportunidades': 'crm',
  '/resultados': 'crm',
  '/conversas': 'atendimento',
  '/assistentes': 'atendimento',
  '/agenda': 'agenda',
  '/automacoes': 'automacoes',
  '/conhecimento': 'conhecimento',
};
export const canonicalNativePath = canonicalNavigationPath;
export const nativeForPath = (path: string) => paths['/' + canonicalNativePath(path).split('/')[1]];
export function useNativeModules() {
  const company = useAuthStore((s) => s.company?.id),
    user = useAuthStore((s) => s.user?.id);
  return useQuery({
    queryKey: ['native-modules', company, user],
    queryFn: () =>
      request<
        Array<{ id: NativeId; enabled: boolean; mode: 'managed' | 'compatible'; state: string }>
      >('/modules/native-status'),
    enabled: !!company && !!user,
    refetchInterval: 30000,
    retry: false,
  });
}
export function NativeFeature({ moduleId, children }: { moduleId: NativeId; children: ReactNode }) {
  const query = useNativeModules();
  const canConfigure = useCanConfigure();
  if (query.isPending)
    return (
      <p className="p-5" role="status">
        Verificando ferramenta…
      </p>
    );
  if (query.isError)
    return (
      <div className="p-5" role="alert">
        Não foi possível verificar esta ferramenta.{' '}
        <button className="text-brand" onClick={() => void query.refetch()}>
          Tentar novamente
        </button>
      </div>
    );
  if (!query.data.find((m) => m.id === moduleId)?.enabled)
    return (
      <div className="p-5">
        <p>
          Esta ferramenta está desativada ou depende de outra ferramenta indisponível. Seus dados
          foram preservados.
        </p>
        {canConfigure ? (
          <Link className="text-brand" to={toolPreparationPath(moduleId)}>
            Revisar esta ferramenta
          </Link>
        ) : (
          <p>Solicite a revisão ao administrador da empresa.</p>
        )}
      </div>
    );
  return children;
}
export function NativeRouteBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation(),
    id = nativeForPath(pathname);
  return id ? <NativeFeature moduleId={id}>{children}</NativeFeature> : children;
}
